import React, { useState, useRef, useCallback } from 'react';
import api from '../../services/api';
import { formatFileSize } from '../../utils/formatFileSize';
import './ImageSection.css';

// Parse image_date from either "2024-06-15" or "2024-06-15T00:00:00.000Z" into YYYY-MM-DD
function toDateString(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function ImageSection({ reflectionId, images, onImagesChange, readOnly, ensureReflection }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);
  const editContainerRef = useRef(null);

  const acceptTypes = '.jpg,.jpeg,.png,.gif,.webp,.heic,.heif';

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);

    try {
      const id = await ensureReflection();
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }

      const response = await api.post(
        `/upload/reflection/${id}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      onImagesChange([...images, ...response.data.images]);
    } catch (err) {
      console.error('Image upload failed:', err);
      if (err.response?.status === 413) {
        setUploadError('Storage limit reached. Please delete some content or contact your administrator about upgrading your plan.');
      } else {
        setUploadError(err.response?.data?.error || 'Failed to upload images');
      }
    } finally {
      setUploading(false);
    }
  }, [ensureReflection, images, onImagesChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    handleFiles(e.target.files);
    // Reset input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleFiles]);

  const handleDelete = useCallback(async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/upload/reflection/${reflectionId}/images/${imageId}`);
      onImagesChange(images.filter(img => img.id !== imageId));
    } catch (err) {
      console.error('Image delete failed:', err);
      alert('Failed to delete image: ' + (err.response?.data?.error || err.message));
    }
  }, [reflectionId, images, onImagesChange]);

  const handleMetadataUpdate = useCallback(async (imageId, field, value) => {
    try {
      const response = await api.put(
        `/upload/reflection/${reflectionId}/images/${imageId}`,
        { [field]: value }
      );
      onImagesChange(images.map(img =>
        img.id === imageId ? { ...img, ...response.data } : img
      ));
    } catch (err) {
      console.error('Image metadata update failed:', err);
    }
  }, [reflectionId, images, onImagesChange]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const lightboxPrev = useCallback(() => {
    setLightboxIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const lightboxNext = useCallback(() => {
    setLightboxIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  return (
    <div className="image-section">
      {/* Upload area - only show in edit mode */}
      {!readOnly && (
        <div
          className={`image-upload-zone ${dragActive ? 'drag-active' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {uploading ? (
            <div className="image-upload-status">Uploading...</div>
          ) : (
            <div className="image-upload-prompt">
              <span className="image-upload-icon">+</span>
              <span>Drop images here or click to browse</span>
              <span className="image-upload-hint">JPEG, PNG, GIF, WebP, HEIC</span>
            </div>
          )}
        </div>
      )}

      {uploadError && (
        <div className="image-upload-error">{uploadError}</div>
      )}

      {/* Gallery grid */}
      {images.length > 0 && (
        <div className="image-gallery">
          {images.map((img, index) => (
            <div key={img.id} className="image-gallery-item">
              <div className="image-gallery-thumb" onClick={() => openLightbox(index)}>
                <img src={img.image_url} alt={img.title || 'Reflection image'} />
              </div>
              <div className="image-gallery-meta">
                {!readOnly && editingId === img.id ? (
                  <div
                    ref={editContainerRef}
                    onBlur={(e) => {
                      // Only close editing if focus leaves the entire edit container
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        handleMetadataUpdate(img.id, 'title', e.currentTarget.querySelector('input[type="text"]').value);
                        setEditingId(null);
                      }
                    }}
                  >
                    <input
                      className="image-meta-input"
                      type="text"
                      placeholder="Add title..."
                      defaultValue={img.title || ''}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleMetadataUpdate(img.id, 'title', e.target.value);
                          setEditingId(null);
                        }
                      }}
                      autoFocus
                    />
                    <input
                      className="image-meta-input image-meta-date"
                      type="date"
                      defaultValue={toDateString(img.image_date)}
                      onChange={(e) => handleMetadataUpdate(img.id, 'image_date', e.target.value || null)}
                    />
                  </div>
                ) : (
                  <div
                    className="image-meta-display"
                    onClick={() => !readOnly && setEditingId(img.id)}
                  >
                    <span className="image-meta-title">{img.title || (readOnly ? 'Untitled' : 'Click to add title...')}</span>
                    <div className="image-meta-row">
                      {img.image_date ? (
                        <span className="image-meta-date-display">
                          {new Date(toDateString(img.image_date) + 'T00:00:00').toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </span>
                      ) : !readOnly ? (
                        <span className="image-meta-date-display image-meta-placeholder">Add date...</span>
                      ) : <span />}
                      {img.file_size && (
                        <span className="image-meta-size">{formatFileSize(img.file_size)}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {!readOnly && (
                <button
                  className="image-delete-btn"
                  onClick={() => handleDelete(img.id)}
                  title="Delete image"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && readOnly && (
        <p className="no-content">No images</p>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && images[lightboxIndex] && (
        <div className="image-lightbox" onClick={closeLightbox}>
          <div className="image-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-lightbox-close" onClick={closeLightbox}>&times;</button>
            {images.length > 1 && (
              <>
                <button className="image-lightbox-prev" onClick={lightboxPrev}>&lsaquo;</button>
                <button className="image-lightbox-next" onClick={lightboxNext}>&rsaquo;</button>
              </>
            )}
            <img
              src={images[lightboxIndex].image_url}
              alt={images[lightboxIndex].title || 'Reflection image'}
            />
            {(images[lightboxIndex].title || images[lightboxIndex].image_date) && (
              <div className="image-lightbox-caption">
                {images[lightboxIndex].title && (
                  <span className="image-lightbox-title">{images[lightboxIndex].title}</span>
                )}
                {images[lightboxIndex].image_date && (
                  <span className="image-lightbox-date">
                    {new Date(toDateString(images[lightboxIndex].image_date) + 'T00:00:00').toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </span>
                )}
              </div>
            )}
            <div className="image-lightbox-counter">
              {lightboxIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageSection;
